import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatDateTime, logout } from "@/lib/clinic";
import { Logo } from "@/components/Logo";
import { AlertTriangle, ArrowLeft, KeyRound, Loader2, LogOut, ShieldCheck } from "lucide-react";

export default function Security() {
  const [form, setForm] = useState({ vecchia: "", nuova: "", conferma: "" });
  const [info, setInfo] = useState(null);
  const [attesa, setAttesa] = useState(false);
  const [errore, setErrore] = useState("");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    api.get("/auth/info").then(({ data }) => setInfo(data)).catch(() => {});
  }, []);

  const salva = async (e) => {
    e.preventDefault();
    setErrore("");
    if (form.nuova !== form.conferma) {
      setErrore("La nuova password e la conferma non coincidono");
      return;
    }
    setAttesa(true);
    try {
      await api.post("/auth/password", { vecchia: form.vecchia, nuova: form.nuova });
      toast.success("Password aggiornata: tutti i dispositivi sono stati disconnessi");
      setTimeout(() => window.location.replace("/login?cambiata=1"), 1400);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setErrore(typeof detail === "string" ? detail : "Impossibile aggiornare la password");
    } finally {
      setAttesa(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-30 bg-white/90 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-[1100px] mx-auto px-5 sm:px-8 py-4 flex flex-wrap items-center gap-4 justify-between">
          <div className="flex items-center gap-4">
            <Logo className="h-8" />
            <div className="h-9 w-px bg-slate-200" />
            <div>
              <h1 className="font-display text-xl sm:text-2xl font-bold tracking-tight text-slate-900 leading-none">
                Sicurezza accesso
              </h1>
              <span className="text-[10px] sm:text-xs uppercase tracking-[0.28em] text-slate-400">
                Password dello studio
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button asChild variant="outline" className="rounded-full" data-testid="security-back-link">
              <Link to="/"><ArrowLeft className="w-4 h-4 mr-2" /> Torna alla gestione</Link>
            </Button>
            <Button
              variant="outline"
              className="rounded-full border-rose-200 text-rose-700 hover:bg-rose-50"
              onClick={logout}
              data-testid="security-logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" /> Esci
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-[1100px] mx-auto px-5 sm:px-8 py-8 grid grid-cols-1 lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3 rounded-xl border border-slate-200 bg-white p-6 sm:p-7">
          <div className="flex items-center gap-2 text-slate-500">
            <KeyRound className="w-4 h-4" />
            <span className="text-[11px] font-semibold uppercase tracking-[0.22em]">Cambio password</span>
          </div>
          <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900 mt-2">
            Aggiorna la password di accesso
          </h2>
          <p className="text-sm text-slate-600 mt-1.5">
            Serve la password attuale. Al salvataggio tutte le sessioni attive, su qualsiasi
            dispositivo, vengono chiuse immediatamente.
          </p>

          <form onSubmit={salva} className="mt-6 space-y-4 max-w-md" data-testid="security-form">
            {[
              ["vecchia", "Password attuale", "current-password"],
              ["nuova", "Nuova password", "new-password"],
              ["conferma", "Conferma nuova password", "new-password"],
            ].map(([k, label, autoComplete]) => (
              <div key={k} className="space-y-1.5">
                <Label className="text-[11px] uppercase tracking-widest text-slate-500">{label}</Label>
                <Input
                  type="password"
                  value={form[k]}
                  onChange={set(k)}
                  autoComplete={autoComplete}
                  data-testid={`security-${k}-input`}
                  className="h-11"
                />
              </div>
            ))}

            {errore && (
              <div
                data-testid="security-error-message"
                className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700"
              >
                {errore}
              </div>
            )}

            <Button
              type="submit"
              disabled={attesa || !form.vecchia || !form.nuova || !form.conferma}
              data-testid="security-submit-btn"
              className="rounded-full px-6 h-11"
            >
              {attesa ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
              Aggiorna password
            </Button>
          </form>
        </div>

        <div className="lg:col-span-2 space-y-4">
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-5">
            <div className="flex items-start gap-2.5 text-amber-800">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <div className="text-sm leading-relaxed">
                <div className="font-semibold">Disconnessione totale</div>
                Dopo il cambio, ogni postazione e ogni schermo dovranno inserire la nuova password.
                Comunicala al personale prima di procedere.
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-5 space-y-3" data-testid="security-info">
            <div className="text-[11px] uppercase tracking-widest text-slate-500">Stato accesso</div>
            <Riga label="Titolare" value={info?.titolare} />
            <Riga label="Ultimo cambio" value={info?.aggiornata_at ? formatDateTime(info.aggiornata_at) : "—"} />
            <Riga label="Versione sessioni" value={info ? `#${info.session_version}` : "—"} />
            <div className="text-xs text-slate-500 pt-1 leading-relaxed">
              La password è salvata solo come hash bcrypt. La sessione viaggia su cookie HttpOnly,
              Secure e SameSite=Strict, non leggibile da JavaScript.
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

const Riga = ({ label, value }) => (
  <div className="flex items-baseline justify-between gap-3">
    <span className="text-xs text-slate-500">{label}</span>
    <span className="font-medium text-slate-900 text-sm break-all text-right">{value || "—"}</span>
  </div>
);
